// 카드에 마우스를 올리면 영상을 잠깐 틀어 주는 데 필요한 것들.
// 2026-10-07 "UI 컨셉은 넷플릭스와 동일" 지시.
//
// 넷플릭스는 타일에 머물면 잠시 뒤 예고편이 소리 없이 돈다. 같은 결로 맞춘다.
//   · 스쳐 지나가는 것으로는 켜지지 않게 HOVER_DELAY 만큼 머물러야 시작한다.
//   · PREVIEW_MS 만큼만 돌고 스스로 꺼져 썸네일로 돌아간다.
//   · 소리는 끈다. 브라우저가 소리 있는 자동재생을 막기도 하고,
//     목록을 훑는 중에 소리가 나면 성가시다.

// 머무는 시간이 이만큼 지나야 튼다.
export const HOVER_DELAY = 600
// 트는 길이. "10초 정도" 지시.
export const PREVIEW_MS = 10000

const YOUTUBE = /(?:youtube\.com\/(?:shorts\/|watch\?v=|embed\/|live\/)|youtu\.be\/)([\w-]{6,})/
const NAVER_TV = /tv\.naver\.com\/(?:v|embed)\/(\d+)/

// 네이버TV 가 미리보기를 못 하는 이유를 화면에 알려 줄 때 쓰는 문구.
export const NO_PREVIEW_NOTE = '네이버TV는 미리보기가 지원되지 않습니다.'

/**
 * 콘텐츠가 영상이면 그 정보를, 아니면 null 을 준다.
 *
 * 지금 데이터에 있는 영상은 두 가지다(2026-10-07 개발 API 기준) —
 * 유튜브 쇼츠 6건, 네이버TV 클립 7건. 블로그·뉴스는 영상이 아니라 그대로 둔다.
 *
 * ── playable 은 왜 있나 ─────────────────────────────────────────────
 * **네이버TV 는 자동재생이 아예 안 된다.** 2026-10-07 에 여덟 가지로 띄워 봤다 —
 * autoPlay/mute 를 어떻게 조합해도 재생 버튼이 뜬 채 멈춰 있고,
 * m.tv.naver.com · /h/embed 같은 다른 경로는 아예 안 뜬다.
 * 유튜브와 달리 재생을 명령할 IFrame API 도 없다.
 *
 * 그래서 네이버TV 는 플레이어를 띄우지 않는다. 띄워 봐야 눌리지도 않는
 * (팝오버의 iframe 은 pointer-events 가 꺼져 있다) 죽은 재생 버튼만 남는다.
 * 대신 큰 썸네일과 NO_PREVIEW_NOTE 를 보여 준다(2026-10-07 결정).
 *
 * @param {object} item 미디어 허브 콘텐츠
 * @returns {{ kind: 'youtube'|'navertv', src: string, id: string, playable: boolean }|null}
 */
export function videoEmbed(item) {
  const url = item?.originalUrl || ''
  if (!url) return null

  const yt = url.match(YOUTUBE)
  if (yt) {
    const id = yt[1]
    // loop + playlist 는 한 벌이다. playlist 없이 loop 만 주면 안 돈다.
    // 쇼츠는 1분 안쪽이라 10초를 다 채우려면 되돌아야 한다.
    // enablejsapi 는 아래 playWhenReady 가 쓴다.
    const q = [
      'autoplay=1',
      'mute=1',
      'controls=0',
      'playsinline=1',
      'rel=0',
      'modestbranding=1',
      'loop=1',
      `playlist=${id}`,
      'enablejsapi=1',
    ].join('&')
    return { kind: 'youtube', id, playable: true, src: `https://www.youtube.com/embed/${id}?${q}` }
  }

  const nv = url.match(NAVER_TV)
  if (nv) {
    const id = nv[1]
    // 주소는 남겨 둔다 — 나중에 네이버가 자동재생을 열어 주면 playable 만 켜면 된다.
    return {
      kind: 'navertv',
      id,
      playable: false,
      src: `https://tv.naver.com/embed/${id}?autoPlay=true&muted=true`,
    }
  }

  return null
}

/**
 * 유튜브에 "틀어라"를 한 번 더 보낸다.
 *
 * autoplay=1 만으로는 안 도는 경우가 있다(브라우저 자동재생 정책, 탭이 포커스를
 * 잃은 상태 등). IFrame API 는 postMessage 한 줄이라 스크립트를 안 불러도 된다 —
 * enablejsapi=1 로 띄운 창에 명령을 던지면 된다.
 *
 * 언제 준비됐는지 알 수 없어 몇 번 나눠 보낸다. 이미 돌고 있으면 무시된다.
 *
 * @param {HTMLIFrameElement} frame
 * @returns {() => void} 더 보내지 않게 멈추는 함수
 */
export function playWhenReady(frame) {
  const send = (func) => {
    try {
      frame?.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func, args: [] }),
        'https://www.youtube.com',
      )
    } catch {
      // 창이 사라졌거나 아직 안 떴다. 다음 차례에 다시 보낸다.
    }
  }
  const timers = [300, 900, 1800, 3000].map((ms) =>
    window.setTimeout(() => {
      send('mute')
      send('playVideo')
    }, ms),
  )
  return () => timers.forEach(window.clearTimeout)
}
